import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Upload } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const CATEGORIES = ["Parfum", "Accessoire téléphone", "Bracelet", "Lunettes", "Écouteurs", "Montre", "Autre"];
const EMPTY = { name: "", description: "", category: "Parfum", image_url: "", stock: 0, points_reward: 0, price: 0 };

const fullImageUrl = (u) => {
  if (!u) return "";
  if (u.startsWith("http")) return u;
  return `${BACKEND_URL}${u}`;
};

export default function AdminProducts() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/products").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const open = (p) => { setEditing(p); setForm(p ? {...p} : EMPTY); setShowForm(true); };
  const close = () => { setEditing(null); setForm(EMPTY); setShowForm(false); };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Sélectionnez une image"); return; }
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image_url: data.url }));
      toast.success("Image téléversée");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Échec de l'envoi");
    }
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, stock: parseInt(form.stock) || 0, points_reward: parseInt(form.points_reward) || 0, price: parseFloat(form.price) || 0, featured: !!form.featured };
      if (editing && editing.id) {
        await api.put(`/admin/products/${editing.id}`, payload);
        toast.success("Produit mis à jour");
      } else {
        await api.post("/admin/products", payload);
        toast.success("Produit créé");
      }
      close();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Supprimer "${p.name}" ?`)) return;
    await api.delete(`/admin/products/${p.id}`);
    toast.success("Produit supprimé");
    load();
  };

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-12" data-testid="admin-products-page">
      <div className="fade-up flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow mb-3">Catalogue</div>
          <h1 className="font-display text-5xl">Produits</h1>
        </div>
        <button onClick={() => open(null)} className="bg-[#d4af37] text-black px-6 py-3 text-xs uppercase tracking-widest hover:bg-[#e6c27a] inline-flex items-center gap-2" data-testid="add-product-btn">
          <Plus size={16}/> Ajouter
        </button>
      </div>

      <div className="border border-[rgba(253,251,247,0.08)] mt-10 overflow-x-auto fade-up-delay-1">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[rgba(253,251,247,0.08)] text-left">
              <th className="px-4 py-3 label-eyebrow">Image</th>
              <th className="px-4 py-3 label-eyebrow">Nom</th>
              <th className="px-4 py-3 label-eyebrow">Catégorie</th>
              <th className="px-4 py-3 label-eyebrow">Stock</th>
              <th className="px-4 py-3 label-eyebrow">Prix</th>
              <th className="px-4 py-3 label-eyebrow">Pts/achat</th>
              <th className="px-4 py-3 label-eyebrow text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-[#a3a8a4]">Aucun produit. Ajoutez-en un.</td></tr>}
            {items.map((p) => (
              <tr key={p.id} className="border-b border-[rgba(253,251,247,0.05)] last:border-0 hover:bg-[#1a241b]/30" data-testid={`admin-product-${p.id}`}>
                <td className="px-4 py-3">
                  {p.image_url ? <img src={p.image_url} alt="" className="w-12 h-12 object-cover"/> : <div className="w-12 h-12 bg-[#1a241b]"/>}
                </td>
                <td className="px-4 py-3">{p.name} {p.featured && <span className="ml-2 text-xs gold-text">★</span>}</td>
                <td className="px-4 py-3 text-[#a3a8a4]">{p.category}</td>
                <td className="px-4 py-3 gold-text">{p.stock}</td>
                <td className="px-4 py-3">{p.price ? `${p.price.toFixed(2)} €` : "—"}</td>
                <td className="px-4 py-3 gold-text">+{p.points_reward || 0}</td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex gap-2">
                    <button onClick={() => open(p)} className="p-2 border border-[rgba(253,251,247,0.1)] hover:border-[#d4af37]" data-testid={`edit-product-${p.id}`}><Pencil size={14}/></button>
                    <button onClick={() => remove(p)} className="p-2 border border-[rgba(253,251,247,0.1)] hover:border-red-500 hover:text-red-400" data-testid={`delete-product-${p.id}`}><Trash2 size={14}/></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-md z-50 flex items-start md:items-center justify-center p-4 overflow-y-auto" onClick={close} data-testid="product-form-modal">
          <form onSubmit={save} onClick={(e)=>e.stopPropagation()} className="w-full max-w-lg bg-[#0a0e0b] border border-[rgba(253,251,247,0.08)] p-8 space-y-4 my-8 max-h-[calc(100vh-4rem)] overflow-y-auto">
            <h3 className="font-display text-3xl mb-2">{editing ? "Modifier" : "Ajouter"} un produit</h3>
            <Input label="Nom" value={form.name} onChange={(v)=>setForm({...form, name:v})} testid="product-name"/>
            <Textarea label="Description" value={form.description} onChange={(v)=>setForm({...form, description:v})} testid="product-description"/>
            <div className="grid grid-cols-2 gap-4">
              <label className="block">
                <span className="label-eyebrow block mb-2">Catégorie</span>
                <select value={form.category} onChange={(e)=>setForm({...form, category:e.target.value})} className="w-full bg-[#050706] border border-[rgba(253,251,247,0.1)] px-4 py-3" data-testid="product-category">
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </label>
                <Input label="Stock" type="number" value={form.stock} onChange={(v)=>setForm({...form, stock:v})} testid="product-stock"/>
            </div>
            <Input label="Points gagnés à l'achat" type="number" value={form.points_reward} onChange={(v)=>setForm({...form, points_reward:v})} testid="product-points-reward"/>
            <Input label="Prix (€)" type="number" value={form.price} onChange={(v)=>setForm({...form, price:v})} testid="product-price"/>
            <label className="flex items-center gap-3 cursor-pointer pt-2">
              <input type="checkbox" checked={!!form.featured} onChange={(e)=>setForm({...form, featured:e.target.checked})} data-testid="product-featured" className="accent-[#d4af37] w-4 h-4"/>
              <span className="text-sm">Mettre en avant sur la page d'accueil</span>
            </label>
            <div className="space-y-2">
              <span className="label-eyebrow block">Photo du produit</span>
              {form.image_url && (
                <img src={fullImageUrl(form.image_url)} alt="aperçu" className="w-32 h-32 object-cover border border-[rgba(253,251,247,0.1)]"/>
              )}
              <label className="flex items-center justify-center gap-2 border border-dashed border-[rgba(253,251,247,0.2)] px-4 py-3 text-xs uppercase tracking-widest cursor-pointer hover:border-[#d4af37] hover:text-[#d4af37] transition-colors" data-testid="product-upload-label">
                <Upload size={14}/> {form.image_url ? "Remplacer la photo" : "Téléverser une photo"}
                <input type="file" accept="image/*" onChange={handleUpload} className="hidden" data-testid="product-upload-input"/>
              </label>
              <div className="text-[10px] text-[#a3a8a4]">Ou collez une URL d'image existante ci-dessous</div>
            </div>
            <Input label="URL de l'image (optionnel)" value={form.image_url} onChange={(v)=>setForm({...form, image_url:v})} testid="product-image-url" required={false}/>
            <div className="flex gap-3 pt-4">
              <button type="submit" className="flex-1 bg-[#d4af37] text-black px-5 py-3 text-xs uppercase tracking-widest hover:bg-[#e6c27a]" data-testid="product-save">Enregistrer</button>
              <button type="button" onClick={close} className="flex-1 border border-[rgba(253,251,247,0.1)] px-5 py-3 text-xs uppercase tracking-widest hover:border-[#d4af37]" data-testid="product-cancel">Annuler</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function Input({ label, type="text", value, onChange, testid, required=true }) {
  return (
    <label className="block">
      <span className="label-eyebrow block mb-2">{label}</span>
      <input type={type} value={value} onChange={(e)=>onChange(e.target.value)} required={required} data-testid={testid} className="w-full bg-[#050706] border border-[rgba(253,251,247,0.1)] px-4 py-3 focus:border-[#d4af37] focus:outline-none"/>
    </label>
  );
}
function Textarea({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="label-eyebrow block mb-2">{label}</span>
      <textarea value={value} onChange={(e)=>onChange(e.target.value)} rows={3} data-testid={testid} className="w-full bg-[#050706] border border-[rgba(253,251,247,0.1)] px-4 py-3 focus:border-[#d4af37] focus:outline-none"/>
    </label>
  );
}
